import { useState, useEffect, useCallback, useRef } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  Play,
  Filter
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OPERATORS = [
  { value: 'eq', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'lt', label: 'Less Than' },
  { value: 'gte', label: 'Greater Than or Equal' },
  { value: 'lte', label: 'Less Than or Equal' },
];

const FIELDS = [
  { value: 'tags', label: 'Tags', type: 'array', ops: ['contains', 'not_contains'] },
  { value: 'total_messages_sent', label: 'Messages Sent', type: 'number', ops: ['eq', 'gt', 'lt', 'gte', 'lte'] },
  { value: 'first_name', label: 'First Name', type: 'string', ops: ['eq', 'contains', 'starts_with'] },
  { value: 'last_name', label: 'Last Name', type: 'string', ops: ['eq', 'contains', 'starts_with'] },
  { value: 'email', label: 'Email', type: 'string', ops: ['eq', 'contains', 'starts_with'] },
  { value: 'phone', label: 'Phone', type: 'string', ops: ['contains'] },
  { value: 'suburb', label: 'Suburb', type: 'string', ops: ['eq', 'contains', 'starts_with'] },
  { value: 'total_amount_spent_cents', label: 'Total Amount Spent (cents)', type: 'number', ops: ['eq', 'gt', 'lt', 'gte', 'lte'] },
  { value: 'opt_in_status', label: 'Subscription Status', type: 'boolean', ops: ['eq'] },
];

export default function SegmentsPage() {
  const { getToken } = useTenant();
  const dashData = useDashboardData();
  const preloadedRaw = dashData?.segmentsSummary?.segments;
  const preloadedSegments = preloadedRaw?.segments || preloadedRaw;
  const hasPreloaded = Array.isArray(preloadedSegments) && preloadedSegments.length > 0;
  const [segments, setSegments] = useState(hasPreloaded ? preloadedSegments : []);
  const [loading, setLoading] = useState(!hasPreloaded);
  const [error, setError] = useState(null);
  const skipInitialFetch = useRef(hasPreloaded);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '',
    rules: { operator: 'AND', conditions: [] }
  });
  
  // Preview State
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Delete State
  const [deleteId, setDeleteId] = useState(null);

  const fetchSegments = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/segments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch segments');
      const data = await response.json();
      setSegments(data.segments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }
    fetchSegments();
  }, [fetchSegments]);

  const handleOpenModal = (segment = null) => {
    setPreviewData(null);
    if (segment) {
      setCurrentSegment(segment);
      setFormData({
        name: segment.name,
        description: segment.description || '',
        rules: segment.rules || { operator: 'AND', conditions: [] }
      });
    } else {
      setCurrentSegment(null);
      setFormData({ 
        name: '', 
        description: '',
        rules: { operator: 'AND', conditions: [{ field: 'tags', operator: 'contains', value: '' }] }
      });
    }
    setIsModalOpen(true);
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      rules: {
        ...prev.rules,
        conditions: [...prev.rules.conditions, { field: 'tags', operator: 'contains', value: '' }]
      }
    }));
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      rules: {
        ...prev.rules,
        conditions: prev.rules.conditions.filter((_, i) => i !== index)
      }
    }));
  };

  const updateCondition = (index, field, value) => {
    setFormData(prev => {
      const newConditions = [...prev.rules.conditions];
      newConditions[index] = { ...newConditions[index], [field]: value };
      
      // Reset operator if field changes to incompatible type
      if (field === 'field') {
        const fieldConfig = FIELDS.find(f => f.value === value);
        newConditions[index].operator = fieldConfig.ops[0];
        newConditions[index].value = '';
      }
      
      return {
        ...prev,
        rules: { ...prev.rules, conditions: newConditions }
      };
    });
  };

  const fetchPreview = async () => {
    setPreviewLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/segments/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Preview failed');
      const data = await response.json();
      setPreviewData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = await getToken();
      const url = currentSegment 
        ? `${BACKEND_URL}/api/segments/${currentSegment.id}`
        : `${BACKEND_URL}/api/segments`;
      
      const method = currentSegment ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to save segment');
      
      fetchSegments();
      setIsModalOpen(false);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/segments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete segment');

      setSegments(segments.filter(s => s.id !== id));
      setDeleteId(null);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="segments-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Segments</h1>
          <p className="text-gray-600 mt-1">Create dynamic groups of customers for targeted campaigns</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Segment
        </button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-3"><Skeleton className="h-5 w-32" /><Skeleton className="h-4 w-48" /><Skeleton className="h-3 w-20" /></div>)}
        </div>
      ) : segments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No segments yet</h3>
          <p className="text-gray-500 mb-4">Create your first segment to target specific customers.</p>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <Plus className="w-4 h-4" />
            Create Segment
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {segments.map((segment) => (
            <div key={segment.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-900 truncate pr-2">{segment.name}</h3>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleOpenModal(segment)}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setDeleteId(segment.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <p className="text-sm text-gray-500 mb-4 line-clamp-2 min-h-[2.5rem]">
                {segment.description || 'No description'}
              </p>

              <div className="mt-auto pt-4 border-t border-gray-50">
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  <Filter className="w-3 h-3" />
                  {segment.rules?.conditions?.length || 0} conditions
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onKeyDown={(e) => { if (e.key === 'Escape') setIsModalOpen(false); }} onClick={(e) => { if (e.target === e.currentTarget) setIsModalOpen(false); }}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" tabIndex={-1}>
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg font-semibold">{currentSegment ? 'Edit Segment' : 'New Segment'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Segment Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="e.g., VIP Customers"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="High value customers who opted in"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium text-gray-900">Rules</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Match</span>
                    <select
                      value={formData.rules.operator}
                      onChange={(e) => setFormData({ ...formData, rules: { ...formData.rules, operator: e.target.value } })}
                      className="text-sm border-gray-300 rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500"
                    >
                      <option value="AND">All (AND)</option>
                      <option value="OR">Any (OR)</option>
                    </select>
                    <span className="text-sm text-gray-500">conditions</span>
                  </div>
                </div>

                <div className="space-y-3 bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200">
                  {formData.rules.conditions.map((condition, index) => (
                    <div key={index} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-start">
                      <select
                        value={condition.field}
                        onChange={(e) => updateCondition(index, 'field', e.target.value)}
                        className="flex-1 text-sm border-gray-300 rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      >
                        {FIELDS.map(f => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>

                      <select
                        value={condition.operator}
                        onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                        className="sm:w-32 text-sm border-gray-300 rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      >
                         {FIELDS.find(f => f.value === condition.field)?.ops.map(op => (
                           <option key={op} value={op}>{OPERATORS.find(o => o.value === op)?.label || op}</option>
                         ))}
                      </select>

                      <div className="flex gap-2 flex-1">
                        <input
                          type="text"
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                          className="flex-1 text-sm border-gray-300 rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500"
                          placeholder="Value"
                        />

                        <button
                          type="button"
                          onClick={() => removeCondition(index)}
                          className="p-2 text-gray-400 hover:text-red-500 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addCondition}
                    className="text-sm text-orange-600 font-medium hover:text-orange-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add Condition
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-800 font-medium">Estimated Audience</span>
                  <button
                    type="button"
                    onClick={fetchPreview}
                    disabled={previewLoading}
                    className="text-xs bg-white border border-blue-200 text-blue-700 px-3 py-1 rounded hover:bg-blue-50 flex items-center gap-1"
                  >
                    {previewLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                    Calculate
                  </button>
                </div>
                {previewData && (
                  <div className="mt-2">
                    <p className="text-2xl font-bold text-blue-900">{previewData.count} Customers</p>
                    {previewData.sample_customers?.length > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        Including: {previewData.sample_customers.map(c => c.first_name || c.phone).join(', ')}...
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
              >
                Save Segment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onKeyDown={(e) => { if (e.key === 'Escape') setDeleteId(null); }} onClick={(e) => { if (e.target === e.currentTarget) setDeleteId(null); }}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center" tabIndex={-1}>
             <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
               <Trash2 className="w-6 h-6 text-red-600" />
             </div>
             <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Segment?</h3>
             <p className="text-gray-500 mb-6">Are you sure you want to delete this segment? This will not delete any customers.</p>
             <div className="flex gap-3 justify-center">
               <button
                 onClick={() => setDeleteId(null)}
                 className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
               >
                 Cancel
               </button>
               <button
                 onClick={() => handleDelete(deleteId)}
                 className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
               >
                 Delete
               </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
}
